import React, { useRef, useEffect, useState } from 'react';

interface AutoExpandingTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  minHeight?: number;
  maxHeight?: number;
  className?: string;
}

/**
 * A textarea component that automatically expands its height based on content,
 * while respecting minimum and maximum height constraints.
 */
export default function AutoExpandingTextarea({
  value,
  onChange,
  minHeight = 80, // Default min height in pixels
  maxHeight = 300, // Default max height in pixels
  className = '',
  ...props
}: AutoExpandingTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [textareaHeight, setTextareaHeight] = useState<number>(minHeight);

  // Adjust height when content changes
  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to auto to get the correct scrollHeight
      textareaRef.current.style.height = 'auto';
      
      // Calculate the new height based on scrollHeight
      const scrollHeight = textareaRef.current.scrollHeight;
      
      // Apply height constraints
      const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));
      
      // Set the new height
      setTextareaHeight(newHeight);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [value, minHeight, maxHeight]);

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={onChange}
      style={{ 
        height: `${textareaHeight}px`,
        resize: 'none', // Disable manual resizing since we're handling it
        overflowY: textareaHeight >= maxHeight ? 'auto' : 'hidden' // Only show scrollbar if at max height
      }}
      className={`transition-height duration-100 ${className}`}
      {...props}
    />
  );
}
